import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  // Check if we're on auth pages
  const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup';
  if (isAuthPage) return null;

  // Define nav links based on authentication
  const getNavLinks = () => {
    if (isAuthenticated) {
      const links = [
        { path: '/dashboard', label: 'Dashboard', icon: 'ti-home' },
        { path: '/visits', label: 'My Visits', icon: 'ti-calendar' },
        { path: '/sales', label: 'My Sales', icon: 'ti-chart-bar' },
        { path: '/workplan', label: 'Work Plan', icon: 'ti-clipboard' },
        { path: '/reports', label: 'Reports', icon: 'ti-chart-pie' },
      ];
      
      if (user?.role === 'admin') {
        links.push({ path: '/admin/users', label: 'Admin', icon: 'ti-settings' });
      }
      
      return links;
    } else {
      return [
        { path: '/', label: 'Home', icon: 'ti-home' },
        { path: '/about', label: 'About', icon: 'ti-info-circle' },
        { path: '/products', label: 'Products', icon: 'ti-packages' },
        { path: '/contact', label: 'Contact us', icon: 'ti-phone' },
      ];
    }
  };

  const navLinks = getNavLinks();

  return (
    <>
      {/* Gradient accent line - always visible */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 51,
        height: 3,
        background: 'linear-gradient(90deg, #1D9E75 0%, #185FA5 100%)',
      }} />

      <nav style={{
        position: 'sticky', top: 3, zIndex: 50,
        background: '#ffffff',
        borderBottom: '1.5px solid #E1F5EE',
        boxShadow: scrolled ? '0 4px 24px rgba(15,110,86,.08)' : 'none',
        transition: 'box-shadow .2s',
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 1.5rem',
          height: 68,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>

          {/* ── Logo - ALWAYS VISIBLE ── */}
          <Link 
            to={isAuthenticated ? "/dashboard" : "/"} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 10, 
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#E8F8F2',
            }}>
              <img 
                src="/images/pharma_logo.webp" 
                alt="K.K. Pharmaceuticals" 
                style={{ 
                  width: 40, 
                  height: 40, 
                  objectFit: 'contain' 
                }} 
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 16,
                fontWeight: 700,
                color: '#085041',
              }}>K.K.Pharmaceuticals</span>
              
            </div>
          </Link>

          {/* ── Desktop Navigation Links ── */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 4,
            flex: 1,
            justifyContent: 'center',
          }} className="hidden-mobile">
            {navLinks.map(({ path, label, icon }) => (
              <NavLink 
                key={path} 
                to={path} 
                style={({ isActive }) => ({
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: isActive ? '#085041' : '#4B5563',
                  background: isActive ? '#E1F5EE' : 'transparent',
                  padding: '8px 16px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  transition: 'all .15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                })}
              >
                <i className={`ti ${icon}`} style={{ fontSize: 16 }} aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </div>

          {/* ── Desktop Right Side ── */}
          {/* <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            flexShrink: 0,
          }} className="hidden-mobile">
            {isAuthenticated ? (
              // ── Logged in: Show user info + logout ──
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '4px 12px 4px 8px',
                  borderRadius: 20,
                  background: '#F3F4F6',
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: '#1D9E75',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                  }}>
                    {user?.fullName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: 13, 
                      fontWeight: 600, 
                      color: '#1F2937',
                      lineHeight: 1.2,
                    }}>
                      {user?.fullName || user?.username || 'User'}
                    </div>
                    <div style={{ 
                      fontSize: 10, 
                      color: '#6B7280',
                      lineHeight: 1.2,
                    }}>
                      {user?.role === 'admin' ? 'Administrator' : 'Medical Representative'}
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <span style={{
                      fontSize: 8,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 12,
                      background: '#8B5CF6',
                      color: '#fff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      Admin
                    </span>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#DC2626',
                    background: '#FEE2E2',
                    border: '1.5px solid #DC2626',
                    padding: '8px 16px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all .15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#DC2626';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FEE2E2';
                    e.currentTarget.style.color = '#DC2626';
                  }}
                >
                  <i className="ti ti-logout" style={{ fontSize: 16 }} aria-hidden="true" />
                  Logout
                </button>
              </>
            ) : (
              // ── Logged out: Show login button ──
              <Link 
                to="/signin" 
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: '#085041',
                  background: '#E1F5EE',
                  border: '1.5px solid #1D9E75',
                  padding: '8px 20px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  transition: 'all .15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <i className="ti ti-login" style={{ fontSize: 14 }} aria-hidden="true" />
                Login
              </Link>
            )}
          </div> */}

          {/* ── Mobile Hamburger ── */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="show-mobile"
            style={{
              width: 40,
              height: 40,
              borderRadius: 9,
              border: '1.5px solid #E1F5EE',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`ti ${isOpen ? 'ti-x' : 'ti-menu-2'}`} style={{ fontSize: 22, color: '#085041' }} aria-hidden="true" />
          </button>
        </div>

        {/* ── Mobile Drawer ── */}
        {isOpen && (
          <div style={{
            borderTop: '1px solid #E1F5EE',
            padding: '12px 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
            {/* Mobile User Info (only when logged in) */}
            {isAuthenticated && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 12px 16px',
                borderBottom: '1px solid #E5E7EB',
                marginBottom: 4,
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: '#1D9E75',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 600,
                }}>
                  {user?.fullName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </div>
                <div>
                  <div style={{ 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: '#1F2937',
                    lineHeight: 1.3,
                  }}>
                    {user?.fullName || user?.username || 'User'}
                  </div>
                  <div style={{ 
                    fontSize: 12, 
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    {user?.role === 'admin' ? (
                      <>
                        <span style={{
                          fontSize: 9,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 12,
                          background: '#8B5CF6',
                          color: '#fff',
                          textTransform: 'uppercase',
                        }}>
                          Admin
                        </span>
                        <span>• Administrator</span>
                      </>
                    ) : (
                      'Medical Representative'
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Navigation Links */}
            {navLinks.map(({ path, label, icon }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setIsOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 9,
                  fontSize: 14,
                  fontWeight: 500,
                  color: isActive ? '#085041' : '#4B5563',
                  background: isActive ? '#E1F5EE' : 'transparent',
                  textDecoration: 'none',
                  transition: 'all .15s',
                })}
              >
                <i className={`ti ${icon}`} style={{ fontSize: 18, color: '#1D9E75' }} aria-hidden="true" />
                {label}
              </NavLink>
            ))}

            {/* Mobile Auth Button - Login/Signup for mobile hamburger menu */}
            {/* {isAuthenticated ? (
              <button
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 9,
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#DC2626',
                  background: '#FEE2E2',
                  border: 'none',
                  width: '100%',
                  cursor: 'pointer',
                  marginTop: 8,
                  transition: 'all .15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#DC2626';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FEE2E2';
                  e.currentTarget.style.color = '#DC2626';
                }}
              >
                <i className="ti ti-logout" style={{ fontSize: 18 }} aria-hidden="true" />
                Logout
              </button>
            ) : (
              <Link
                to="/signin"
                onClick={() => setIsOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 9,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1D9E75',
                  background: '#E8F8F2',
                  textDecoration: 'none',
                  marginTop: 8,
                }}
              >
                <i className="ti ti-login" style={{ fontSize: 18 }} aria-hidden="true" />
                Login / Sign Up
              </Link>
            )} */}
          </div>
        )}
        
      </nav>
    </>
  );
};

export default Navbar;